// When I remove a note, remember to go back into each existing note and set the key down by1

import "./Main.css";
import React, { MouseEventHandler } from "react";

import NewNoteButton from "../Main/NewNoteButton/NewNoteButton";
import NewNoteCreator from "./NewNoteCreator/NewNoteCreator";
import NotesDisplayer from "./NotesDisplayer/NotesDisplayer";
import NoteEditor from "./NoteEditor/NoteEditor";

import { noteTemplate } from "./Templates/noteTemplate";

const convertToSavableFormat = (notes: Map<number, noteTemplate>): string => {
    let stringNotes: Map<number, string> = new Map<number, string>;

    notes.forEach((noteTemp: noteTemplate, key: number) => {
        const stringified = JSON.stringify(noteTemp);
        stringNotes.set(key, stringified);
    });

    const objectToReturn: string = JSON.stringify(Object.fromEntries(stringNotes));

    return objectToReturn;
}

const convertToUsableFormat = (notes: string): Map<number, noteTemplate> => {
    const objEntries = Object.entries(JSON.parse(notes));
    let objectToReturn: Map<number, noteTemplate> = new Map<number, noteTemplate>;

    for (const [key, value] of objEntries) {
        const val: noteTemplate = JSON.parse(value as string);
        val["Date Created"] = new Date(val["Date Created"]);
        objectToReturn.set(parseInt(key), val);
    }

    return objectToReturn;
}

const retrievedData = localStorage.getItem("data");
const notes: Map<number, noteTemplate> = retrievedData ? convertToUsableFormat(retrievedData) : new Map<number, noteTemplate>();

let noteEditData: noteTemplate | undefined = undefined;

const newNoteTemplate = {
    "Title": "",
    "Note": "",
    "Date Created": new Date(),
}

const updateNoteHandler = {
    "functionsToRunWhenNotesAreUpdated": new Array <Function> ,
    "updateNotesWithTemplate": (template: noteTemplate) => {
        notes.set(notes.size + 1, template);
        localStorage.setItem("data", convertToSavableFormat(notes));

        updateNoteHandler.functionsToRunWhenNotesAreUpdated.forEach((fn: Function) => {
            fn();
        });
    },
    "replaceNotesWithTemplate": (template: noteTemplate, oldData: noteTemplate | undefined) => {
        let newKey: number = -1;

        notes.forEach((value: noteTemplate, key: number) => {
            if (JSON.stringify(value) == JSON.stringify(oldData)) {
                newKey = key;
            }
        })

        if (newKey) {
            notes.set(newKey, template);
            localStorage.setItem("data", convertToSavableFormat(notes));

            const createdNoteBlock: HTMLElement = document.getElementById(newKey.toString()) as HTMLElement;
            const childrenOfCNB = createdNoteBlock.childNodes;

            childrenOfCNB.forEach((value, key) => {
                switch (key) {
                    case 0:
                        const title: HTMLElement = value.firstChild as HTMLElement;
                        title.innerHTML = template.Title;
                        break;
                    case 1:
                        const note: HTMLElement = value.firstChild as HTMLElement;
                        note.innerHTML = template.Note;
                        break;
                    case 2:
                        const dateCreated: HTMLElement = value.firstChild as HTMLElement;
                        dateCreated.innerHTML = template["Date Created"].toLocaleString();
                        break;
                
                    default:
                        break;
                }
            });

            updateNoteHandler.functionsToRunWhenNotesAreUpdated.forEach((fn: Function) => {
                fn();
            });
        }

        noteEditData = undefined;
    }
}

function addNote(noteInfo: Map<string, string>) {
    const template = { ...newNoteTemplate };

    template.Title = noteInfo.get("NoteTitle") as string;
    template.Note = noteInfo.get("NoteText") as string;
    template["Date Created"] = new Date();

    updateNoteHandler.updateNotesWithTemplate(template);
}

function editNote(newNoteInfo: Map<string, string>) {
    const template = { ...newNoteTemplate };

    template.Title = newNoteInfo.get("NoteTitle") as string;
    template.Note = newNoteInfo.get("NoteText") as string;
    template["Date Created"] = new Date();

    updateNoteHandler.replaceNotesWithTemplate(template, noteEditData);
}

function Main(): JSX.Element {
    const [newNoteCreatorStatus, setNewNoteCreatorStatus] = React.useState("Neutral");
    const [noteEditorStatus, setNoteEditorStatus] = React.useState("Neutral");

    const newNoteButtonOnClick = () => {
        if (newNoteCreatorStatus !== "Neutral" || noteEditorStatus !== "Neutral") return;
        setNewNoteCreatorStatus("OpeningNewNote");
    }
    
    const noteButtonWasClickedNoteDisplayer = React.useCallback((noteInfo: noteTemplate, key: number) => {
        if (noteEditorStatus !== "Neutral" || newNoteCreatorStatus !== "Neutral") return;
        setNoteEditorStatus("OpeningNoteEditor");
        noteEditData = noteInfo;

        (document.querySelector("#noteTitleInput") as HTMLInputElement).value = noteInfo.Title;
        (document.querySelector("#noteTextInput") as HTMLInputElement).value = noteInfo.Note;
    }, []);

    const NewNoteCallback = React.useCallback(() => {
        setNewNoteCreatorStatus("ClosingNewNote");

        setTimeout(() => {
            setNewNoteCreatorStatus("Neutral");

            (document.querySelector(".noteTitleInput") as HTMLInputElement).value = "";
            (document.querySelector(".noteTextInput") as HTMLInputElement).value = "";
        }, 1000);
    }, []);

    const finishNewNoteCallback = React.useCallback(() => {
        const noteTitleContent: string = (document.querySelector(".noteTitleInput") as HTMLInputElement).value as string;
        const noteTextContent: string = (document.querySelector(".noteTextInput") as HTMLInputElement).value as string;

        if (!noteTitleContent || !noteTextContent) {
            return;
        }

        NewNoteCallback();

        const returnedStuff = new Map<string, string>;

        returnedStuff.set("NoteTitle", noteTitleContent);
        returnedStuff.set("NoteText", noteTextContent);
        addNote(returnedStuff);
    }, []);    

    const finishNoteEditorCallback = React.useCallback(() => {
        const noteTitleContent: string = (document.querySelector("#noteTitleInput") as HTMLInputElement).value as string;
        const noteTextContent: string = (document.querySelector("#noteTextInput") as HTMLInputElement).value as string;

        if (noteTitleContent && noteTextContent) {

        } else if (!noteTitleContent || !noteTextContent) {
            // We are gonna delete
            // nevermind i got too lazy to add this function, maybe later

            return; // Sorry guys, too lazy..
        }

        NoteEditCallBack();

        const returnedStuff = new Map<string, string>;

        returnedStuff.set("NoteTitle", noteTitleContent);
        returnedStuff.set("NoteText", noteTextContent);
        editNote(returnedStuff);
    }, []);

    function NoteEditCallBack() {
        setNoteEditorStatus("ClosingNoteEditor");

        setTimeout(() => {
            setNoteEditorStatus("Neutral");
        }, 1000);
    }

    const keyListener = React.useCallback((event: KeyboardEvent) => {
        if (event.key === "Enter") {
            event.preventDefault();

            finishNewNoteCallback();
        } else if (event.key === "Escape") {
            event.preventDefault();

            NewNoteCallback();
        }
    }, []);

    const openNewNoteListener = React.useCallback((event: KeyboardEvent) => {
        if (event.key === "n" && newNoteCreatorStatus === "Neutral") {
            console.log("Opening key pressed");
            event.preventDefault();
            newNoteButtonOnClick();
        }
    }, []);

    React.useEffect(() => {
        if (newNoteCreatorStatus === "OpeningNewNote") {
            document.addEventListener("keydown", keyListener);
        } else {
            document.removeEventListener("keydown", keyListener);
        }
    }, [newNoteCreatorStatus]);

    React.useEffect(() => {
        if (noteEditorStatus === "Neutral") {
            document.addEventListener("keyup", openNewNoteListener);
        } else {
            document.removeEventListener("keyup", openNewNoteListener);
        }
    }, [noteEditorStatus])

    React.useCallback(NewNoteCallback, []);
    React.useCallback(finishNewNoteCallback, []);

    return (
        <section id="mainDiv" className="main">
            <NewNoteButton onClick={newNoteButtonOnClick}/>
            <NewNoteCreator noteStatus={newNoteCreatorStatus} finishNewNoteCallback={finishNewNoteCallback} discardNewNoteCallback={NewNoteCallback}/> 
            <NoteEditor noteEditStatus={noteEditorStatus} finishNoteEditCallback={finishNoteEditorCallback} discardNoteEditCallback={NoteEditCallBack} noteData={noteEditData}/>
            <NotesDisplayer notesData={notes} noteButtonWasClicked={noteButtonWasClickedNoteDisplayer}/>
        </section>
    )
}

export default Main;

/* 
    help
    please

*/